import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { apiGet, operationsClient } from '@/lib/api';
import { getContactDisplayName } from '@/lib/contacts';
import { formatPhoneDisplay } from '@/lib/contacts/phone';
import { cn } from '@/lib/utils';
import { NO_AUTOFILL_PROPS } from '@/lib/forms/no-autofill';

export interface CustomerSelectorContactResult {
  id?: string;
  _id?: string;
  contactId?: string;
  firstName?: string;
  lastName?: string;
  title?: string | null;
  displayName?: string;
  primaryEmail?: string;
  primaryPhone?: string;
  /** Human stored as the primary person on a commercial account/location. */
  primaryContactName?: string;
  primaryContactTitle?: string;
  companyName?: string;
  contactType?: string;
  /** QB parent-account id — set when this contact is a branch/sub-customer. */
  parentContactId?: string | null;
  /** Lean parent-account snapshot so a branch row can name its company. */
  parentContact?: {
    id: string;
    displayName?: string | null;
    companyName?: string | null;
    contactType?: string | null;
  } | null;
  /** Branch/linked-contact count — >0 marks this row as a company with locations. */
  childCount?: number;
  addresses?: Array<{
    /** ContactAddress id — present on the operations list response; lets
     * pickers hand an exact saved-address identity to downstream records. */
    id?: string;
    street?: string;
    /** Unit/suite. Carried so picking a saved address keeps the exact line the
     * contact has on file, and so two units at one street/ZIP stay distinct. */
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    /** Defaults to US only when the contact has none — never forced. */
    country?: string;
    isPrimary?: boolean;
    /** Purpose flags — consumed downstream to pick the service vs billing line. */
    isService?: boolean;
    isBilling?: boolean;
    accessNotes?: string;
    type?: string;
  }>;
}

export interface CustomerSelectorSelectedContact {
  id: string;
  name: string;
  primaryEmail?: string;
  primaryPhone?: string;
  primaryContactName?: string;
  primaryContactTitle?: string;
  address?: string;
}

interface CustomerSelectorProps {
  selected: CustomerSelectorSelectedContact | null;
  onSelect: (contact: CustomerSelectorContactResult) => void;
  onClear: () => void;
  placeholder?: string;
  selectedLayout?: 'stacked' | 'inline';
  /** Keep results in document flow when hosted inside a scrolling dialog. */
  resultsLayout?: 'dropdown' | 'inline';
  query?: string;
  onQueryChange?: (query: string) => void;
  emptyMessage?: string;
  /** Renders a "New customer" row at the foot of the dropdown. */
  onCreateNew?: () => void;
  readOnly?: boolean;
  /**
   * Inline layout only: render the selected card as name + avatar only,
   * omitting the address/email/phone lines. Opt-in (default `false`) so the
   * shared inline card stays unchanged for lead / credit memo / membership /
   * visit flows where it's the only place those details show. The estimate
   * uses it because email/phone live in its Contact details selectors. (NOT-219)
   */
  nameOnly?: boolean;
}

function initials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function CustomerSelector({
  selected,
  onSelect,
  onClear,
  placeholder = 'Search by name, email, or phone...',
  selectedLayout = 'stacked',
  resultsLayout = 'dropdown',
  query: controlledQuery,
  onQueryChange,
  emptyMessage = 'No contacts found',
  onCreateNew,
  readOnly = false,
  nameOnly = false,
}: CustomerSelectorProps) {
  const [internalQuery, setInternalQuery] = useState('');
  const [results, setResults] = useState<CustomerSelectorContactResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Tracks whether the empty-query "recent contacts" preload has run so a
  // focus that finds nothing loaded fetches once, then is served from state.
  const preloadedRef = useRef(false);
  const query = controlledQuery ?? internalQuery;

  function handleQueryChange(value: string) {
    if (controlledQuery === undefined) {
      setInternalQuery(value);
    }
    onQueryChange?.(value);
  }

  const fetchContacts = useCallback(async (search: string) => {
    setSearching(true);
    setFailed(false);
    try {
      const trimmed = search.trim();
      const res = await apiGet<
        | {
            data?: CustomerSelectorContactResult[];
            contacts?: CustomerSelectorContactResult[];
          }
        | CustomerSelectorContactResult[]
      >(
        operationsClient,
        // With nothing typed this is the "who might you mean?" list, and it
        // should offer accounts — the people attached to a location are not
        // who an estimate is addressed to. Once a name is typed, search
        // everyone: the person you are looking for may well be a site contact
        // who has since become a customer, and a search that hides them reads
        // as "we do not have them".
        `/contacts?${trimmed ? `search=${encodeURIComponent(trimmed)}&` : 'kind=CUSTOMER&'}limit=10`,
      );
      const items = Array.isArray(res)
        ? res
        : ((res as { data?: CustomerSelectorContactResult[] }).data ??
          (res as { contacts?: CustomerSelectorContactResult[] }).contacts ??
          []);
      setResults(items);
      setShowResults(true);
    } catch {
      // A failure is not an empty address book. Say so, offer a retry, and let
      // the caller ask again — a request that timed out behind a full
      // connection pool must not leave the picker claiming there is nobody.
      setResults([]);
      setFailed(true);
      setShowResults(true);
      preloadedRef.current = false;
    } finally {
      setSearching(false);
    }
  }, []);

  // Preload most-recent contacts on focus (NOT-283): show options immediately
  // without typing. Runs once; subsequent focuses reuse the cached results.
  const handleFocus = useCallback(() => {
    if (query.trim().length >= 2) {
      if (results.length > 0) setShowResults(true);
      return;
    }
    if (preloadedRef.current) {
      setShowResults(true);
      return;
    }
    void fetchContacts('').then(() => {
      preloadedRef.current = true;
    });
  }, [query, results.length, fetchContacts]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!query.trim() || query.trim().length < 2) {
      return;
    }
    timerRef.current = setTimeout(() => {
      void fetchContacts(query);
    }, 300);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, fetchContacts]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node))
        setShowResults(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (selected) {
    if (selectedLayout === 'inline') {
      return (
        <div className="flex min-h-10 items-center gap-3 rounded-md border bg-card px-3 py-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
            {initials(selected.name)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{selected.name}</p>
            {!nameOnly && selected.primaryContactName && (
              <p className="truncate text-xs text-muted-foreground">
                Primary contact: {selected.primaryContactName}
                {selected.primaryContactTitle ? ` · ${selected.primaryContactTitle}` : ''}
              </p>
            )}
            {!nameOnly && selected.address && (
              <p className="truncate text-xs text-muted-foreground">{selected.address}</p>
            )}
            {!nameOnly && selected.primaryEmail && (
              <p className="truncate text-xs text-muted-foreground">{selected.primaryEmail}</p>
            )}
            {!nameOnly && selected.primaryPhone && (
              <p className="truncate text-xs text-muted-foreground">
                {formatPhoneDisplay(selected.primaryPhone)}
              </p>
            )}
          </div>
          {!readOnly && (
            <Button
              variant="ghost"
              size="icon-sm"
              type="button"
              title="Change customer"
              aria-label="Change customer"
              onClick={onClear}
              className="shrink-0"
            >
              <X className="size-3.5" />
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-1.5">
        {!readOnly && (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => {
                handleQueryChange('');
                onClear();
              }}
            >
              Change
            </Button>
          </div>
        )}
        <div className="flex items-center gap-3 rounded-md border bg-card px-3 py-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
            {initials(selected.name)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{selected.name}</p>
            {selected.primaryContactName && (
              <p className="text-xs text-muted-foreground">
                Primary contact: {selected.primaryContactName}
                {selected.primaryContactTitle ? ` · ${selected.primaryContactTitle}` : ''}
              </p>
            )}
            {selected.address && (
              <p className="text-xs text-muted-foreground">{selected.address}</p>
            )}
            {selected.primaryEmail && (
              <p className="text-xs text-muted-foreground">{selected.primaryEmail}</p>
            )}
            {selected.primaryPhone && (
              <p className="text-xs text-muted-foreground">
                {formatPhoneDisplay(selected.primaryPhone)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const createNewRow = (
    <button
      type="button"
      className="flex w-full items-center gap-2 border-t px-3 py-2.5 text-left text-sm font-medium text-blue-800 transition-colors hover:bg-accent"
      /*
       * The press and the action are separate handlers on purpose.
       *
       * `onMouseDown` only stops the default — that is what keeps the input
       * from blurring and closing this dropdown before the click can land.
       * Doing the WORK there too meant the row worked for a mouse and for
       * nothing else: Enter and Space fire `click`, never `mousedown`, so
       * anyone arriving by keyboard or screen reader tabbed onto a button
       * that did nothing. Preventing the default on mousedown does not
       * suppress the click, so a pointer press still runs this exactly once.
       */
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        setShowResults(false);
        onCreateNew?.();
      }}
    >
      <Plus size={14} />
      New customer
    </button>
  );

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          placeholder={placeholder}
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={handleFocus}
          className="pl-8"
          // The placeholder names "email" and "phone", so Chrome offers its own
          // autofill list on top of ours and leaves it hanging there while the
          // form scrolls. This is a contact picker, not a field to autofill.
          {...NO_AUTOFILL_PROPS}
        />
        {searching && (
          <Loader2
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground"
          />
        )}
      </div>

      {showResults && results.length > 0 && (
        <div
          className={cn(
            'mt-1 w-full rounded-md border bg-popover shadow-lg overflow-y-auto',
            resultsLayout === 'dropdown' ? 'absolute z-50 max-h-48' : 'max-h-96',
          )}
        >
          {results.map((c) => {
            const cId = c.id ?? c.contactId ?? c._id ?? '';
            const cName = getContactDisplayName(c);
            const parentName = c.parentContact?.companyName || c.parentContact?.displayName || null;
            // Company (has branches) vs branch (has a parent) vs plain — surfaced
            // so the picker reads as a hierarchy, not a flat list (ServiceTitan
            // customer → location model).
            const hierarchyHint = parentName
              ? `Location of ${parentName}`
              : c.childCount && c.childCount > 0
                ? `${c.childCount} ${c.childCount === 1 ? 'location' : 'locations'}`
                : null;
            return (
              <button
                key={cId}
                type="button"
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                onClick={() => {
                  handleQueryChange('');
                  onSelect(c);
                  setShowResults(false);
                }}
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {initials(cName)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{cName}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {[c.primaryEmail, formatPhoneDisplay(c.primaryPhone)]
                      .filter(Boolean)
                      .join(' · ') || 'No contact info'}
                  </p>
                  {hierarchyHint && (
                    <p className="text-xs font-medium text-primary truncate">{hierarchyHint}</p>
                  )}
                </div>
              </button>
            );
          })}
          {onCreateNew ? createNewRow : null}
        </div>
      )}

      {showResults && !searching && results.length === 0 && (
        <div
          className={cn(
            'mt-1 w-full rounded-md border bg-popover shadow-lg',
            resultsLayout === 'dropdown' && 'absolute z-50',
          )}
        >
          {failed ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-muted-foreground">Could not load contacts.</p>
              <button
                type="button"
                className="mt-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
                onClick={() => void fetchContacts(query)}
              >
                Try again
              </button>
            </div>
          ) : (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">{emptyMessage}</p>
          )}
          {onCreateNew ? createNewRow : null}
        </div>
      )}
    </div>
  );
}
